import { useEffect, useState } from 'react';

/**
 * Returns Date.now() updated every `intervalMs` ms.
 * Use as `const now = useLiveTime()` in components displaying elapsed time.
 */
export const useLiveTime = (intervalMs = 1000) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
};

/**
 * Format elapsed time between `iso` timestamp and `now` (ms).
 * - <60s   →  "12s"  (increments each second)
 * - <60m   →  "3d 24s"  or "3d"  (drops seconds after 10 min for calm display)
 * - <24h   →  "2s 15d"
 * - >=24h  →  "2 gün"
 * Also includes the exact HH:MM:SS clock time.
 */
export const formatLiveElapsed = (iso, now) => {
  if (!iso) return { exact: '', elapsed: '' };
  const d = new Date(iso);
  const diffMs = Math.max(0, (now || Date.now()) - d.getTime());
  const secs = Math.floor(diffMs / 1000);

  const exact = d.toLocaleTimeString('az-AZ', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  let elapsed;
  if (secs < 60) {
    elapsed = `${secs}s`;
  } else if (secs < 3600) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    elapsed = s > 0 && m < 10 ? `${m}d ${s}s` : `${m}d`;
  } else if (secs < 86400) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    elapsed = m > 0 ? `${h}s ${m}d` : `${h}s`;
  } else {
    const days = Math.floor(secs / 86400);
    elapsed = `${days} gün`;
  }

  return { exact, elapsed };
};

/**
 * Current wall-clock string, updates every second when used with useLiveTime.
 * Format: "01 iyul 2026 • 14:23:07 (Baku)"
 */
export const formatNowClock = (now) => {
  const d = new Date(now || Date.now());
  const date = d.toLocaleDateString('az-AZ', { day: '2-digit', month: 'long', year: 'numeric' });
  const time = d.toLocaleTimeString('az-AZ', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  return { date, time };
};
